<template>
  <div class="activity-log-container">
    <div class="activity-log-header">
      <h2>Registro de Actividades</h2>
      <p class="activity-log-description">
        Historial de acciones y eventos del sistema
      </p>
    </div>

    <!-- Filter Section -->
    <div class="activity-log-filters">
      <div class="filter-group">
        <label for="person-filter">
          <span class="material-icons">person</span>
          Filtrar por persona:
        </label>
        <div class="custom-select-wrapper">
          <div
            class="custom-select"
            @click="toggleDropdown"
            :class="{ active: isDropdownOpen }"
          >
            <div class="selected-option">
              <span class="option-text">
                {{ selectedPersonDisplay || 'Todas las personas' }}
              </span>
              <span class="material-icons dropdown-arrow">
                {{ isDropdownOpen ? 'expand_less' : 'expand_more' }}
              </span>
            </div>
            <div class="dropdown-options" v-show="isDropdownOpen">
              <div
                class="dropdown-option"
                :class="{ selected: !selectedPerson }"
                @click.stop="selectOption('')"
              >
                <span class="option-text">Todas las personas</span>
              </div>
              <div
                v-for="person in uniquePersonsWithStations"
                :key="person.id"
                class="dropdown-option"
                :class="{
                  selected: selectedPerson.toString() === person.id.toString(),
                }"
                @click.stop="selectOption(person.id.toString())"
              >
                <span class="option-text">
                  {{ person.name
                  }}{{ person.station ? ` (${person.station})` : '' }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="filter-actions">
        <div class="filter-info" v-if="selectedPerson">
          <span class="filter-result-count">
            {{ filteredActivities.length }} actividades encontradas
          </span>
        </div>
        <button
          @click="clearFilter"
          class="clear-filter-btn"
          v-if="selectedPerson"
        >
          <span class="material-icons">clear</span>
          Limpiar filtro
        </button>
        <button
          @click="exportToExcel"
          class="export-btn"
          :disabled="activities.length === 0"
        >
          <span class="material-icons">file_download</span>
          Exportar CSV
        </button>
      </div>
    </div>
    <div class="activity-log-content">
      <div v-if="activities.length === 0" class="placeholder-content">
        <span class="material-icons placeholder-icon">history</span>
        <p>No hay actividades que registrar</p>
      </div>
      <div v-else class="activities-list">
        <div
          v-for="activity in sortedActivities"
          :key="activity.id"
          class="activity-item"
          :class="activity.type"
        >
          <div class="activity-icon">
            <span class="material-icons">{{
              getActivityIcon(activity.type, activity.subtype)
            }}</span>
          </div>
          <div class="activity-content">
            <div class="activity-time">
              {{ formatDateTime(activity.timestamp) }}
            </div>
            <div class="activity-type">
              {{ getActivityTypeLabel(activity.type) }}
            </div>
            <div class="activity-description">
              {{ getUpdatedDescription(activity) }}
            </div>
            <div v-if="activity.details" class="activity-details">
              <template
                v-for="(value, detailKey) in activity.details"
                :key="detailKey"
              >
                <span v-if="detailKey !== 'personId'" class="detail-item">
                  <strong>{{ formatDetailKey(detailKey) }}: </strong>
                  <span v-if="detailKey === 'personName'">{{
                    getCurrentPersonName(activity)
                  }}</span>
                  <span v-else-if="detailKey === 'station'">{{
                    getCurrentPersonStation(activity)
                  }}</span>
                  <span v-else>{{ value || 'No especificado' }}</span>
                </span>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ActivityLog',
  data() {
    return {
      activities: [],
      selectedPerson: '', // Filter state - now stores person ID
      isDropdownOpen: false, // Custom dropdown state
      personsData: {}, // Current person data from TaskList
    }
  },
  computed: {
    filteredActivities() {
      if (!this.selectedPerson) {
        return this.activities
      }

      return this.activities.filter((activity) => {
        // Check if the activity is related to the selected person using ID
        if (activity.details && activity.details.personId) {
          return (
            activity.details.personId.toString() ===
            this.selectedPerson.toString()
          )
        }
        // Fallback for old activities that might not have personId
        if (activity.details && activity.details.personName) {
          const person = Object.values(this.personsData).find(
            (p) => p.name === activity.details.personName
          )
          return (
            person && person.id.toString() === this.selectedPerson.toString()
          )
        }
        return false
      })
    },

    sortedActivities() {
      const sorted = this.filteredActivities
        .slice()
        .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      return sorted
    },

    uniquePersonsWithStations() {
      const personsMap = new Map()

      this.activities.forEach((activity) => {
        if (activity.details) {
          let personId = null
          let personName = null
          let station = null

          // Try to get person ID first (new format)
          if (activity.details.personId) {
            personId = activity.details.personId
            // Get current name from personsData
            const currentPerson = this.personsData[personId]
            if (currentPerson) {
              personName = currentPerson.name
              station = currentPerson.station
            } else {
              // Fallback to activity name if person not found
              personName = activity.details.personName || 'Persona desconocida'
              station = activity.details.station
            }
          } else if (activity.details.personName) {
            // Fallback for old activities without ID
            personName = activity.details.personName
            station = activity.details.station
            // Try to find ID from current persons data
            const person = Object.values(this.personsData).find(
              (p) => p.name === personName
            )
            if (person) {
              personId = person.id
              station = person.station // Use current station
            }
          }

          if (personId && personName) {
            personsMap.set(personId, {
              id: personId,
              name: personName,
              station: station && station !== 'Sin estación' ? station : null,
            })
          }
        }
      })

      return Array.from(personsMap.values()).sort((a, b) =>
        a.name.localeCompare(b.name)
      )
    },

    selectedPersonDisplay() {
      if (!this.selectedPerson) {
        return ''
      }
      const person = this.uniquePersonsWithStations.find(
        (p) => p.id.toString() === this.selectedPerson.toString()
      )
      return person
        ? `${person.name}${person.station ? ` (${person.station})` : ''}`
        : this.selectedPerson
    },
  },
  mounted() {
    console.log('ActivityLog mounted, loading activities from localStorage')
    this.loadActivities()
    // Set up an interval to refresh activities periodically
    this.refreshInterval = setInterval(() => {
      this.loadActivities()
    }, 1000) // Refresh every second

    // Close dropdown when clicking outside
    this.clickOutsideHandler = (event) => {
      if (!this.$el.contains(event.target)) {
        this.isDropdownOpen = false
      }
    }
    document.addEventListener('click', this.clickOutsideHandler)
  },
  beforeUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler)
    }
  },
  methods: {
    getActivityIcon(type, subtype) {
      const iconMap = {
        person: {
          created: 'person_add',
          updated: 'edit',
          moved: 'swap_horiz',
        },
      }

      return iconMap[type]?.[subtype] || 'info'
    },

    getActivityTypeLabel(type) {
      const typeLabels = {
        person: 'Personal',
      }

      return typeLabels[type] || 'Actividad'
    },

    formatDateTime(timestamp) {
      const date = new Date(timestamp)
      const dateOptions = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }
      const timeOptions = {
        hour: '2-digit',
        minute: '2-digit',
      }

      return `${date.toLocaleDateString(
        'es-ES',
        dateOptions
      )} - ${date.toLocaleTimeString('es-ES', timeOptions)}`
    },

    formatDetailKey(key) {
      const keyMap = {
        personName: 'Persona',
        taskName: 'Tablero',
        role: 'Rol',
        station: 'Estación',
        fromTask: 'Tablero origen',
        toTask: 'Tablero destino',
        changes: 'Cambios',
      }

      return keyMap[key] || key
    },

    // Filter methods
    clearFilter() {
      this.selectedPerson = ''
      this.isDropdownOpen = false
    },

    toggleDropdown() {
      this.isDropdownOpen = !this.isDropdownOpen
    },

    selectOption(value) {
      this.selectedPerson = value
      this.isDropdownOpen = false
    },

    // Activity management
    loadActivities() {
      const saved = localStorage.getItem('nexo-vue-activities')
      if (saved) {
        try {
          const activities = JSON.parse(saved)
          // Only update if there are changes to avoid unnecessary re-renders
          if (JSON.stringify(activities) !== JSON.stringify(this.activities)) {
            this.activities = activities
            console.log(
              'ActivityLog - Loaded activities from localStorage:',
              this.activities.length
            )
          }
        } catch (e) {
          console.error('ActivityLog - Error loading activities:', e)
          this.activities = []
        }
      } else {
        this.activities = []
      }

      // Also load current persons data
      this.loadPersonsData()
    },

    loadPersonsData() {
      const saved = localStorage.getItem('nexo-vue-persons-data')
      if (saved) {
        try {
          const personsData = JSON.parse(saved)
          this.personsData = personsData
        } catch (e) {
          console.error('ActivityLog - Error loading persons data:', e)
          this.personsData = {}
        }
      } else {
        this.personsData = {}
      }
    },

    // Get current person name by ID for display
    getCurrentPersonName(activity) {
      if (activity.details && activity.details.personId) {
        const currentPerson = this.personsData[activity.details.personId]
        if (currentPerson) {
          return currentPerson.name
        }
      }
      // Fallback to stored name
      return activity.details?.personName || 'Persona desconocida'
    },

    // Get current person station by ID for display
    getCurrentPersonStation(activity) {
      if (activity.details && activity.details.personId) {
        const currentPerson = this.personsData[activity.details.personId]
        if (currentPerson) {
          return currentPerson.station || 'Sin estación'
        }
      }
      // Fallback to stored station
      return activity.details?.station || 'Sin estación'
    },

    // Generate updated description with current person name
    getUpdatedDescription(activity) {
      const currentName = this.getCurrentPersonName(activity)
      const details = activity.details

      if (activity.type === 'person') {
        if (activity.subtype === 'created') {
          return `Persona "${currentName}" añadida a "${
            details?.taskName || 'Sin tarea'
          }"`
        } else if (activity.subtype === 'updated') {
          return `Persona "${currentName}" actualizada en "${
            details?.taskName || 'Sin tarea'
          }"`
        } else if (activity.subtype === 'moved') {
          return `Persona "${currentName}" movida de "${
            details?.fromTask || 'Sin origen'
          }" a "${details?.toTask || 'Sin destino'}"`
        }
      }

      // Fallback to original description
      return activity.description
    },

    // Export activities to Excel
    exportToExcel() {
      if (this.activities.length === 0) {
        alert('No hay actividades para exportar')
        return
      }

      // Create CSV content (Excel compatible)
      const currentDate = new Date().toLocaleDateString('es-ES')
      const currentTime = new Date().toLocaleTimeString('es-ES')

      let csvContent = 'data:text/csv;charset=utf-8,'

      // Column headers - New structured format
      csvContent += `ID Actividad,Fecha,Hora,Tipo Entidad,ID Persona,Nombre Persona,Estación/Compañía,Acción Realizada,Tablero/Zona Origen,Tablero/Zona Destino,Campo Modificado,Valor Anterior,Valor Nuevo,Rol/Función,Observaciones\n`

      // Activity data with new structured format
      this.sortedActivities.forEach((activity) => {
        const activityData = this.parseActivityForExcel(activity)
        const date = new Date(activity.timestamp).toLocaleDateString('es-ES')
        const time = new Date(activity.timestamp).toLocaleTimeString('es-ES')

        csvContent += `"${activity.id}","${date}","${time}","${activityData.entityType}","${activityData.personId}","${activityData.personName}","${activityData.station}","${activityData.action}","${activityData.originBoard}","${activityData.destinationBoard}","${activityData.modifiedField}","${activityData.oldValue}","${activityData.newValue}","${activityData.role}","${activityData.observations}"\n`
      })

      // Create and download file
      const encodedUri = encodeURI(csvContent)
      const link = document.createElement('a')
      link.setAttribute('href', encodedUri)

      const fileName = `Actividades_Estructuradas_NEXO_${currentDate.replace(
        /\//g,
        '-'
      )}_${currentTime.replace(/:/g, '-')}.csv`
      link.setAttribute('download', fileName)

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },

    // Parse activity data into structured format for Excel
    parseActivityForExcel(activity) {
      const defaultData = {
        entityType: 'Persona',
        personId: activity.details?.personId || '',
        personName: this.getPersonNameFromActivity(activity) || 'N/A',
        station: '',
        action: 'Acción no especificada',
        originBoard: '',
        destinationBoard: '',
        modifiedField: '',
        oldValue: '',
        newValue: '',
        role: '',
        observations: '',
      }

      // Get current person data if available
      if (
        activity.details?.personId &&
        this.personsData[activity.details.personId]
      ) {
        const personData = this.personsData[activity.details.personId]
        defaultData.station = personData.station || ''
        defaultData.role = personData.role || ''
      }

      // Parse based on activity type and subtype
      if (activity.type === 'person') {
        if (activity.subtype === 'created') {
          return {
            ...defaultData,
            action: 'Persona creada',
            destinationBoard: activity.details?.taskName || '',
            role: activity.details?.role || '',
            station: activity.details?.station || '',
            observations: `Nueva persona añadida al sistema`,
          }
        } else if (activity.subtype === 'moved') {
          return {
            ...defaultData,
            action: 'Movimiento entre tableros',
            originBoard: activity.details?.fromTask || '',
            destinationBoard: activity.details?.toTask || '',
            observations: `Persona trasladada de zona`,
          }
        } else if (activity.subtype === 'updated') {
          // Parse the changes from the details
          const changes = activity.details?.changes || ''
          let modifiedField = ''
          let oldValue = ''
          let newValue = ''

          // Extract field changes from the changes string
          if (changes.includes('Nombre:')) {
            modifiedField = 'Nombre'
            const nameMatch = changes.match(/Nombre: (.+?) → (.+?)(?:,|$)/)
            if (nameMatch) {
              oldValue = nameMatch[1]
              newValue = nameMatch[2]
            }
          } else if (changes.includes('Rol:')) {
            modifiedField = 'Rol/Función'
            const roleMatch = changes.match(/Rol: (.+?) → (.+?)(?:,|$)/)
            if (roleMatch) {
              oldValue = roleMatch[1]
              newValue = roleMatch[2]
            }
          } else if (changes.includes('Estación:')) {
            modifiedField = 'Estación/Compañía'
            const stationMatch = changes.match(/Estación: (.+?) → (.+?)(?:,|$)/)
            if (stationMatch) {
              oldValue = stationMatch[1]
              newValue = stationMatch[2]
            }
          }

          return {
            ...defaultData,
            action: 'Actualización de datos',
            destinationBoard: activity.details?.taskName || '',
            modifiedField,
            oldValue,
            newValue,
            observations: changes || 'Datos actualizados',
          }
        }
      }

      // Fallback for unknown activity types
      return {
        ...defaultData,
        action: activity.description || 'Acción desconocida',
        observations: JSON.stringify(activity.details || {}),
      }
    }, // Helper method to get person name from activity
    getPersonNameFromActivity(activity) {
      if (activity.details?.personName) {
        return activity.details.personName
      }
      if (
        activity.details?.personId &&
        this.personsData[activity.details.personId]
      ) {
        return this.personsData[activity.details.personId].name
      }
      return null
    },
  },
}
</script>

<style>
.activity-log-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: #202225;
  position: relative;
}

.activity-log-header {
  padding: 20px 20px 20px 80px;
  background-color: #2f3136;
  border-bottom: 1px solid #40444b;
}

.activity-log-header h2 {
  margin: 0 0 8px 0;
  color: #ffffff;
  font-size: 24px;
}

.activity-log-description {
  margin: 0;
  color: #b9bbbe;
  font-size: 14px;
}

.activity-log-filters {
  padding: 20px 20px 20px 80px;
  background-color: #2f3136;
  border-bottom: 1px solid #40444b;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;
}

.filter-group label {
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
  min-width: fit-content;
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-group label .material-icons {
  font-size: 20px;
  color: #43b581;
}

.custom-select-wrapper {
  position: relative;
  min-width: 280px;
}

.custom-select {
  background-color: #202225;
  border: 1px solid #202225;
  border-radius: 4px;
  color: #dcddde;
  cursor: pointer;
  transition: background-color 0.2s, border-color 0.2s;
  user-select: none;
}

.custom-select:hover {
  background-color: #36393f;
}

.custom-select.active {
  background-color: #36393f;
  border-color: #5865f2;
}

.selected-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  font-size: 14px;
}

.dropdown-arrow {
  color: #b9bbbe;
  font-size: 20px;
  transition: transform 0.2s ease;
}

.custom-select.active .dropdown-arrow {
  transform: rotate(180deg);
}

.dropdown-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: #36393f;
  border: 1px solid #5865f2;
  border-top: none;
  border-radius: 0 0 4px 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.dropdown-option {
  padding: 10px 16px;
  font-size: 14px;
  color: #dcddde;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid #40444b;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover {
  background-color: #40444b;
}

.dropdown-option.selected {
  background-color: #5865f2;
  color: #ffffff;
}

.dropdown-option.selected:hover {
  background-color: #4752c4;
}

.filter-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.filter-info {
  display: flex;
  align-items: center;
}

.filter-result-count {
  color: #43b581;
  font-size: 14px;
  font-weight: 600;
  background-color: rgba(67, 181, 129, 0.1);
  padding: 6px 12px;
  border-radius: 4px;
  border: 1px solid rgba(67, 181, 129, 0.2);
}

.clear-filter-btn {
  background-color: #ed4245;
  border: none;
  border-radius: 4px;
  color: white;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background-color 0.2s, transform 0.1s;
  white-space: nowrap;
}

.clear-filter-btn:hover {
  background-color: #c43a3d;
}

.clear-filter-btn:active {
  transform: translateY(1px);
}

.clear-filter-btn .material-icons {
  font-size: 18px;
}

.export-btn {
  background-color: #2dce89;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.2s, transform 0.1s;
}

.export-btn:hover:not(:disabled) {
  background-color: #1d9863;
}

.export-btn:active:not(:disabled) {
  transform: translateY(1px);
}

.export-btn:disabled {
  background-color: #4f545c;
  color: #72767d;
  cursor: not-allowed;
}

.export-btn .material-icons {
  font-size: 18px;
}

.activity-log-content {
  flex: 1;
  padding: 20px;
}

.placeholder-content {
  text-align: center;
  color: #72767d;
  margin-top: 60px;
}

.placeholder-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.placeholder-content p {
  font-size: 16px;
  margin: 0;
}

.activities-list {
  max-width: 800px;
  margin: 0 auto;
}

.activity-item {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  margin-bottom: 12px;
  background-color: #2f3136;
  border-radius: 8px;
  border-left: 4px solid #7289da;
}

.activity-item.person {
  border-left-color: #43b581;
}

.activity-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  background-color: #5865f2;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.activity-item.person .activity-icon {
  background-color: #43b581;
}

.activity-icon .material-icons {
  color: #ffffff;
  font-size: 20px;
}

.activity-content {
  flex: 1;
  min-width: 0;
}

.activity-time {
  font-size: 12px;
  color: #72767d;
  margin-bottom: 4px;
}

.activity-type {
  font-size: 12px;
  color: #43b581;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.activity-description {
  font-size: 14px;
  color: #dcddde;
  margin-bottom: 8px;
  line-height: 1.4;
}

.activity-details {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.detail-item {
  font-size: 12px;
  color: #b9bbbe;
  background-color: #40444b;
  padding: 4px 8px;
  border-radius: 4px;
}

.detail-item strong {
  color: #ffffff;
}

@media (max-width: 768px) {
  .activity-log-header {
    padding: 16px 16px 16px 70px;
  }

  .activity-log-header h2 {
    font-size: 20px;
  }

  .activity-log-filters {
    flex-direction: column;
    gap: 16px;
    padding: 16px;
  }

  .filter-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .clear-filter-btn,
  .export-btn {
    width: 100%;
    justify-content: center;
  }

  .activity-log-content {
    padding: 20px 16px;
  }

  .activity-item {
    padding: 12px;
    gap: 12px;
  }

  .activity-icon {
    width: 32px;
    height: 32px;
  }

  .activity-icon .material-icons {
    font-size: 16px;
  }

  .activity-details {
    flex-direction: column;
    gap: 8px;
  }
}
</style>
